import React, { useEffect, useRef, useState } from "react";
import { Search, MapPin } from "lucide-react";

const GEOAPIFY_KEY = process.env.REACT_APP_GEOAPIFY_KEY;
// Geoapify autocomplete expects: bias=proximity:lon,lat and filter=countrycode:xx
const DALAT_BIAS = "proximity:108.4583,11.9404";
const COUNTRY_FILTER = "countrycode:vn";

function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

export default function GeoapifyPlaceSearch({
  className = "",
  value = "",
  onChange,
  onSelect,
}) {
  const [keyword, setKeyword] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const abortRef = useRef(null);
  const wrapRef = useRef(null);

  const debouncedKeyword = useDebouncedValue(keyword, 350);

  useEffect(() => {
    setKeyword(value || "");
  }, [value]);

  useEffect(() => {
    const q = String(debouncedKeyword || "").trim();

    if (!q || q.length < 2) {
      setItems([]);
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (!GEOAPIFY_KEY) {
      setItems([]);
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    abortRef.current?.abort?.();
    const controller = new AbortController();
    abortRef.current = controller;

    const run = async () => {
      try {
        setLoading(true);

        const url =
          `https://api.geoapify.com/v1/geocode/autocomplete?` +
          `text=${encodeURIComponent(q)}` +
          `&limit=6` +
          `&lang=vi` +
          `&format=json` +
          `&filter=${encodeURIComponent(COUNTRY_FILTER)}` +
          `&bias=${encodeURIComponent(DALAT_BIAS)}` +
          `&apiKey=${GEOAPIFY_KEY}`;

        const res = await fetch(url, { signal: controller.signal });
        const data = await res.json();

        const results = Array.isArray(data?.results) ? data.results : [];
        setItems(results);
        setOpen(true);
        setActiveIndex(results.length ? 0 : -1);
      } catch (err) {
        if (err.name !== "AbortError") {
          setItems([]);
          setOpen(false);
          setActiveIndex(-1);
        }
      } finally {
        setLoading(false);
      }
    };

    run();

    return () => controller.abort();
  }, [debouncedKeyword]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handlePick = (item) => {
    const nextText = item.formatted || item.address_line1 || item.name || "";

    setKeyword(nextText);
    setItems([]);
    setOpen(false);
    setActiveIndex(-1);

    onChange?.(nextText);
    onSelect?.({
      placeId: item.place_id || "",
      name: item.name || item.address_line1 || nextText,
      address: item.formatted || nextText,
      latitude: Number(item.lat),
      longitude: Number(item.lon),
      raw: item,
    });
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-admin-textMuted"
        />
        <input
          className={`${className} pl-9`}
          value={keyword}
          onChange={(e) => {
            const next = e.target.value;
            setKeyword(next);
            onChange?.(next);
          }}
          onFocus={() => items.length > 0 && setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (open && items.length > 0) {
                const picked = items[activeIndex] || items[0];
                handlePick(picked);
              }
            }

            if (e.key === "ArrowDown") {
              e.preventDefault();
              if (!open && items.length > 0) {
                setOpen(true);
                return;
              }
              setActiveIndex((prev) =>
                items.length ? Math.min(prev + 1, items.length - 1) : -1
              );
            }

            if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIndex((prev) => Math.max(prev - 1, 0));
            }

            if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          placeholder="Tìm địa điểm từ Geoapify..."
        />
      </div>

      {open && (
        <div className="absolute z-[1000] mt-2 max-h-80 w-full overflow-auto rounded-2xl border border-admin-border bg-admin-surface p-2 shadow-soft-xl">
          {loading ? (
            <div className="px-3 py-2 text-sm text-admin-textMuted">Đang tìm…</div>
          ) : items.length === 0 ? (
            <div className="px-3 py-2 text-sm text-admin-textMuted">Không có kết quả</div>
          ) : (
            items.map((item, idx) => (
              <button
                key={`${item.place_id || `${item.lat}-${item.lon}`}-${idx}`}
                type="button"
                onClick={() => handlePick(item)}
                className={`flex w-full items-start gap-3 rounded-xl px-3 py-2 text-left ${
                  idx === activeIndex ? "bg-admin-muted" : "hover:bg-admin-muted/60"
                }`}
              >
                <MapPin size={16} className="mt-0.5 shrink-0 text-admin-primary" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-admin-text">
                    {item.name || item.address_line1 || "Địa điểm"}
                  </div>
                  <div className="line-clamp-2 text-xs text-admin-textMuted">
                    {item.formatted || item.address_line2 || ""}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}